/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import * as React from 'react';
import {Button, Input, Typography} from 'onboarding-components';

import toggles from 'common/onboarding/immersive-onboarding/assets/toggles.webp';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {PieChartReact} from 'common/charts/visualization/pie-chart';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {INDUSTRY_EVENTS, IndustryKey} from 'common/onboarding/immersive-onboarding/constants';
import {updateEventSelection} from 'common/report/queries/app-api/immersive-onboarding';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {isValidEmailAddress} from 'common/util/string';
import {TextInput} from 'common/onboarding/immersive-onboarding/components/text-input';

import styles from './events.module.styl';

type HeaderProps = {title: string; subTitle: string};
type SectionProps = {
  subStepId: number;
  onNext?: () => void;
  onSkip?: () => void;
  events?: string[];
};

const NUM_INVITE_TEAMMATE = 3;

const SectionHeader = ({title, subTitle}: HeaderProps) => {
  return (
    <div>
      <Typography variant='headingXs' style={{margin: 0}}>
        {title}
      </Typography>
      <Typography variant='bodySm'>{subTitle}</Typography>
    </div>
  );
};

const Section = (props: React.PropsWithChildren<HeaderProps & SectionProps>) => {
  const {children, onNext, onSkip, subStepId, ...headerProps} = props;

  return (
    <div className={styles.section}>
      <div className={styles.substep}>{subStepId}</div>
      <div>
        <SectionHeader {...headerProps} />
        {children}
        <div className={styles.footerButtons}>
          <Button onClick={onNext} size='small'>
            Next
          </Button>
          {onSkip && (
            <Button onClick={onSkip} variant={`inline`} includeArrow={false} size='small'>
              Skip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const EventSelector = ({selected, onIndustryKeyChange, events, updateEvents}) => {
  const dropdownOptions = Object.keys(INDUSTRY_EVENTS).map((el) => ({value: el, label: INDUSTRY_EVENTS[el].name}));
  const [addEventString, setAddEventString] = React.useState(``);

  const deleteEvent = (eventIndex) => {
    updateEvents((events) => events.filter((_, index) => index !== eventIndex));
  };

  return (
    <div className={styles.sectionTile}>
      <SelectReact
        sections={[{items: dropdownOptions}]}
        disableSearch
        onChange={(ev) => {
          onIndustryKeyChange({industryKey: ev.detail.item.value as IndustryKey});
        }}
        theme={SelectTheme.Tertiary}
        alwaysShowCaret
        menuWidth={150}
      >
        <div className='custom-trigger' slot={`trigger`}>
          <Button variant='inline' includeArrow={false} size='small'>
            {INDUSTRY_EVENTS[selected].name}
            <SvgIconReact icon='caret-down' style={{minHeight: 17, height: 17}} />
          </Button>
        </div>
      </SelectReact>
      <div className={styles.eventContainer}>
        {events.map((event, eventIndex) => (
          <div key={`event-row-${eventIndex}`} className={styles.eventEditorRow}>
            <Input
              value={event}
              onValueChange={(value) => {
                updateEvents((events) => events.map((event, index) => (index === eventIndex ? value : event)));
              }}
              isFullwidth
              validityStatus={event.trim().length > 0 ? `valid` : `invalid`}
            />
            <div className={styles.deleteIconWrapper} onClick={() => events.length > 1 && deleteEvent(eventIndex)}>
              <SvgIconReact className={styles.deleteIcon} icon={`trashcan`} />
              {events.length === 1 && eventIndex === 0 && (
                <TooltipReact placement={Placement.Top} text={`You must choose at least one event`} />
              )}
            </div>
          </div>
        ))}
        <Input
          key={`addEvent`}
          value={addEventString}
          placeholder={`Add Event`}
          onValueChange={(value) => {
            setAddEventString(value);
          }}
          onBlur={() => {
            if (addEventString.trim().length > 0) {
              updateEvents((events) => [...events, addEventString]);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === `Enter` && addEventString.trim().length > 0) {
              updateEvents((events) => [...events, addEventString]);
              setAddEventString(``);
            }
          }}
          isFullwidth
          validityStatus={addEventString.trim().length > 0 ? `valid` : `invalid`}
        />
      </div>
    </div>
  );
};

// TODO this is just a placeholder
const ChartPreview = ({events}) => {
  return (
    <div className={styles.sectionTile}>
      <PieChartReact
        style={{height: `250px`, width: `250px`}}
        chartData={[
          {
            name: events[0],
            value: 434861,
          },
          {
            name: events[1],
            value: 424843,
          },
          {
            name: events[2],
            value: 325916,
          },
          {
            name: events[3],
            value: 287461,
          },
        ]}
      />
    </div>
  );
};

const ConfirmEventsSection = () => {
  const {setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);

  const [selected, updateSelected] = React.useState<IndustryKey>(() =>
    setupGuideInfo ? setupGuideInfo.industry : `b2b`,
  );

  const [events, updateEvents] = React.useState(INDUSTRY_EVENTS[selected].events);
  // TODO: After creating other substep Sections
  const [, setSubStep] = React.useState<number>(1);

  React.useEffect(() => {
    if (setupGuideInfo !== null) {
      updateSelected(setupGuideInfo.industry);
      updateEvents(
        setupGuideInfo.events.length > 0 ? setupGuideInfo.events : INDUSTRY_EVENTS[setupGuideInfo.industry].events,
      );
    }
  }, [setupGuideInfo]);

  const onIndustryKeyChange = ({industryKey}: {industryKey: IndustryKey}) => {
    updateSelected(industryKey);
    if (setupGuideInfo.events.length === 0 || setupGuideInfo.industry !== industryKey) {
      updateEvents(INDUSTRY_EVENTS[industryKey].events);
    } else updateEvents(setupGuideInfo.events);
  };

  const onNext = () => {
    // setSubStep
    updateEventSelection(setupGuideInfo.project_id, events, selected).then((result) => {
      setSetupGuideInfo(result);
    });
  };

  return (
    <div>
      <div className={styles.overview}>
        <Typography variant='headingXs' style={{margin: 0}}>
          Overview
        </Typography>
        <Typography variant='bodySm'>
          This step by step-by-step guide will help you select the right events to track first
        </Typography>
      </div>
      <Section
        subStepId={1} // TODO: use enum
        title={`Confirm your first events`}
        subTitle={`We recommend tracking fewer events at first. This list is a good starting point for your industry:`}
        onNext={() => {
          onNext();
          setSubStep(2);
        }}
      >
        <div className={styles.eventSelectorSection}>
          <EventSelector
            selected={selected}
            events={events}
            onIndustryKeyChange={onIndustryKeyChange}
            updateEvents={updateEvents}
          />
          <ChartPreview events={events} />
        </div>
      </Section>
    </div>
  );
};

const InviteSection = () => {
  const {setStep} = React.useContext(SetupGuideContext);

  return (
    <Section
      subStepId={2}
      title={`Invite a developer to help you set up Mixpanel — you have unlimited seats`}
      subTitle={`We recommend tracking fewer events at first. This list is a good starting point for your industry:`}
      onSkip={() => {
        setStep(Step.Implement);
      }}
      onNext={() => {
        setStep(Step.Implement);
      }}
    >
      <div className={styles.teammate}>
        {[...Array(NUM_INVITE_TEAMMATE)].map((_, index) => (
          <TextInput key={index} placeholder={`Email address`} validator={(val) => isValidEmailAddress(val)} />
        ))}
      </div>
    </Section>
  );
};

export const Events = () => {
  return (
    <>
      <Header title={`Personalize your set up`} step={`step 1`} imgSrc={toggles} duration={3} />
      <Content>
        {/** replace with banner element */}
        <div />
        <ConfirmEventsSection />
        <InviteSection />
      </Content>
    </>
  );
};
